import type { TreeCollaboratorDto } from "api/types";
import { useDialogFocus } from "hooks/useDialogFocus";
import React from "react";
import { useTranslation } from "react-i18next";

import styles from "./CollaboratorsDialog.module.css";

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

export type CollaboratorsDialogProps = {
	collaborators: TreeCollaboratorDto[];
	onClose: () => void;
};

/**
 * Read-only list of a tree's collaborators (photo, name, email when visible to the viewer, and role).
 * Sharing management stays in ShareTreeDialog; this is the informational view for everyone else.
 */
export const CollaboratorsDialog = ({ collaborators, onClose }: CollaboratorsDialogProps) => {
	const { t } = useTranslation("homePage");
	const dialogRef = useDialogFocus<HTMLDivElement>();

	return (
		<>
			<button
				aria-label={t("collaboratorsDialog.close")}
				className={styles.scrim}
				onClick={onClose}
				type="button"
			/>
			<div
				ref={dialogRef}
				aria-label={t("collaboratorsDialog.title")}
				aria-modal="true"
				className={styles.dialog}
				role="dialog">
				<header className={styles.header}>
					<div className={styles.headerCopy}>
						<h2 className={styles.heading}>{t("collaboratorsDialog.title")}</h2>
						<p className={styles.subheading}>{t("collaboratorsDialog.subtitle")}</p>
					</div>
					<button
						aria-label={t("collaboratorsDialog.close")}
						className={styles.closeButton}
						onClick={onClose}
						type="button">
						×
					</button>
				</header>

				<div className={styles.memberList}>
					{collaborators.map((member: TreeCollaboratorDto) => (
						<div key={member.userId} className={styles.memberRow}>
							<span className={styles.memberAvatar}>
								{member.profileImageUrl ? (
									<img alt="" referrerPolicy="no-referrer" src={member.profileImageUrl} />
								) : (
									getInitials(member.fullName)
								)}
							</span>
							<span className={styles.memberIdentity}>
								<span className={styles.memberName}>{member.fullName}</span>
								{member.email && <span className={styles.memberEmail}>{member.email}</span>}
							</span>
							<span className={styles.roleBadge}>{member.role}</span>
						</div>
					))}
				</div>
			</div>
		</>
	);
};
